import {
  getDeleteApiLabInstitutionIdLabIdMutationOptions,
  type LabViewDTO,
} from '@smartsash/api-client';
import { useEntityDelete } from '@smartsash/server-ops';
import { Avatar, Badge, Icon, openModal } from '@smartsash/ui';
import { Link, useNavigate } from '@tanstack/react-router';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './Laboratories.module.css';

import { useGroupNames, useInstitutionNames } from '@/hooks/useLookupNames';
import { DEVICES_LAB_FIELD, devicesFilteredBy } from '@/ui/pages/Devices/devicesModel';
import { USERS_LAB_FIELD, usersFilteredBy } from '@/ui/pages/Users/usersModel';
import { MobileCardActions, type RowActionEntry } from '@/ui/shared/RowActions';
import { firstNonEmpty } from '@/utils/firstNonEmpty';

/** `LabViewDTO` carries no manager, so the card shows an empty one — as it did before HS-869. */
const MANAGER_NAME = '';

function MobileCard({ row }: { row: LabViewDTO }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // HS-869: the card/menu read the DTO the list query returns.
  const id = row.id ?? '';
  const institutionNames = useInstitutionNames();
  const groupNames = useGroupNames();
  const institute = firstNonEmpty(
    row.institutionId == null ? undefined : institutionNames.get(row.institutionId),
    row.institutionId,
  );
  const group = firstNonEmpty(
    row.groupId == null ? undefined : groupNames.get(row.groupId),
    row.groupId,
  );
  // HS-847: delete via server-ops (invalidates the lab list query + toast).
  // Lab delete is keyed by the composite { institutionId, labId }.
  const del = useEntityDelete({
    entity: 'lab',
    mutationOptions: getDeleteApiLabInstitutionIdLabIdMutationOptions,
    // HS-1141: the deleted record's own reads are dropped, not refetched into a 404.
    deletesRecord: true,
  });

  // HS-1177: the imperative modal replaces a `useState` pair plus a conditionally
  // rendered dialog. It resolves with the pressed action's `value`, or `null` when
  // dismissed via the overlay, Escape or the close button.
  const handleDelete = useCallback(async () => {
    const { action } = await openModal({
      title: t('laboratories.deleteTitle', { name: row.name }),
      description: t('laboratories.deleteDescription'),
      variant: 'error',
      icon: <Icon name="trash-01" />,
      actions: [
        { label: t('common.cancel'), value: 'cancel' },
        { label: t('common.delete'), value: 'confirm', destructive: true },
      ],
    }).closed;

    if (action === 'confirm') del.mutate({ institutionId: row.institutionId ?? '', labId: id });
  }, [del, id, row.institutionId, row.name, t]);

  // HS-840: these three were `() => {}`. The desktop `RowActions` for the same row has always
  // navigated; the mobile card rendered the same menu with the handlers left unwired, so on a
  // phone Edit / View devices / View users opened the menu and did nothing. `no-empty-function`
  // is what surfaced it. The destinations mirror `./RowActions.tsx` exactly.
  const items: RowActionEntry[] = [
    {
      label: t('common.edit'),
      icon: 'edit-01',
      // HS-1396: the keys mirror `./RowActions.tsx`. Note that this card still carries FOUR of
      // that file's seven entries — Add device, View group and View certifications are missing,
      // which is content drift rather than a permission gap and is tracked as HS-1397.
      permission: 'laboratory.edit',
      onClick: () => {
        void navigate({ to: '/laboratories/$id/edit', params: { id } });
      },
    },
    null,
    {
      label: t('laboratories.actions.viewDevices'),
      icon: 'tv-01',
      onClick: () => {
        void navigate({ to: '/devices', search: devicesFilteredBy(DEVICES_LAB_FIELD, id) });
      },
    },
    {
      label: t('laboratories.actions.viewUsers'),
      icon: 'users-02',
      onClick: () => {
        // HS-1240: `GET /api/User/list` takes the laboratory as a GUID, matched on the user's
        // active assignments — see `usersFilteredBy`.
        void navigate({ to: '/users', search: usersFilteredBy(USERS_LAB_FIELD, id) });
      },
    },
    null,
    {
      label: t('common.delete'),
      icon: 'trash-01',
      permission: 'laboratory.delete',
      onClick: () => {
        void handleDelete();
      },
    },
  ];

  return (
    <div className={styles['m-card']}>
      <Link to="/laboratories/$id" params={{ id }} className={styles['m-card-title']}>
        {row.name}
      </Link>
      <div className={styles['m-card-badges']}>
        <Badge variant="modern" size="sm" iconLeading={<Icon name="bank" />}>
          {institute}
        </Badge>
        <Badge variant="modern" size="sm" iconLeading={<Icon name="building-07" />}>
          {group}
        </Badge>
        <Badge variant="modern" size="sm" iconLeading={<Icon name="tv-01" />}>
          {t('laboratories.devicesCount', { count: row.amountDevices ?? 0 })}
        </Badge>
        <Badge variant="modern" size="sm">
          <span className={styles['m-badge-avatar']}>
            <Avatar name={MANAGER_NAME} size="xs" />
          </span>
        </Badge>
      </div>
      <div className={styles['m-card-meta']}>
        <span className={styles['m-card-meta-label']}>{t('laboratories.labManager')}</span>
        <span className={styles['m-card-meta-value']}>{MANAGER_NAME}</span>
      </div>
      <MobileCardActions items={items} />
    </div>
  );
}

export default MobileCard;
